import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CLUSTERS } from "@/lib/constants";
import {
  Search, Building2, UserPlus, ArrowRight, ArrowLeft, Check, Sparkles,
  Loader2, ShieldCheck, ClipboardList,
} from "lucide-react";

const EMPTY = { tipo_ente: "", ragione_sociale: "", codice_fiscale: "", partita_iva: "", indirizzo: "",
  cap: "", comune: "", provincia: "", email: "", pec: "", telefono: "", referente: "", cluster: "", note: "" };

const STEPS = [
  { n: 1, label: "Cliente", icon: Building2 },
  { n: 2, label: "Analisi esigenze", icon: ClipboardList },
  { n: 3, label: "Prodotti", icon: Sparkles },
];

export default function NewPratica() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1 — cliente
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [vatQuery, setVatQuery] = useState("");
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounce = useRef(null);

  // Step 2 — needs analysis
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [savingNa, setSavingNa] = useState(false);

  // Step 3 — suggeriti
  const [suggeriti, setSuggeriti] = useState(null); // null = non compilata (skip)

  useEffect(() => {
    api.get("/needs-analysis/config").then(({ data }) => setQuestions(data.questions || [])).catch(() => {});
  }, []);

  // Ricerca live (debounce)
  useEffect(() => {
    if (creating) return;
    clearTimeout(debounce.current);
    if ((query || "").trim().length < 2) { setResults([]); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/anagrafiche/search`, { params: { q: query } });
        setResults(data);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [query, creating]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const lookupVat = async () => {
    const piva = (vatQuery || "").replace(/\D/g, "");
    if (piva.length !== 11) return toast.error("Inserisci una Partita IVA di 11 cifre");
    setLooking(true);
    try {
      const { data } = await api.get(`/lookup/vat/${piva}`);
      setForm((f) => ({ ...f,
        ragione_sociale: data.ragione_sociale || f.ragione_sociale,
        partita_iva: data.partita_iva || f.partita_iva,
        codice_fiscale: data.codice_fiscale || f.codice_fiscale,
        indirizzo: data.indirizzo || f.indirizzo, cap: data.cap || f.cap,
        comune: data.comune || f.comune, provincia: data.provincia || f.provincia }));
      toast.success(`Dati recuperati da ${data.fonte}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLooking(false); }
  };

  const saveCliente = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/anagrafiche", form);
      if (data.duplicato) {
        toast.info(`Cliente già presente: uso l'anagrafica esistente "${data.ragione_sociale}"`);
      } else {
        toast.success("Nuovo cliente creato");
      }
      pickCliente(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const pickCliente = (a) => {
    setSelected(a);
    setAnswers(a.needs_analysis?.answers || {});
    setStep(2);
  };

  // Step 2 handlers
  const setSingle = (code, value) => setAnswers((a) => ({ ...a, [code]: value }));
  const toggleMulti = (code, value) => setAnswers((a) => {
    const cur = new Set(a[code] || []);
    cur.has(value) ? cur.delete(value) : cur.add(value);
    return { ...a, [code]: [...cur] };
  });

  const answeredCount = useMemo(
    () => questions.filter((q) => {
      const v = answers[q.code];
      return q.type === "multi" ? (v || []).length > 0 : !!v;
    }).length, [questions, answers]);

  const submitNeeds = async () => {
    setSavingNa(true);
    try {
      const { data } = await api.post(`/anagrafiche/${selected.id}/needs-analysis`, { answers });
      setSuggeriti(data.suggeriti || []);
      setStep(3);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingNa(false); }
  };

  const skipNeeds = () => { setSuggeriti(null); setStep(3); };

  const ALL_PRODUCTS = [
    { code: "rc_economia_solidale", name: "RC Economia Solidale", route: "/quota/nuovo" },
    { code: "mrv_volontariato", name: "Tutela Multirischi del Volontariato", route: "/quota/mrv" },
    { code: "infortuni_economia_solidale", name: "Infortuni per l'Economia Solidale", route: "/quota/infortuni" },
    { code: "cat_nat_impresa", name: "CAT NAT Assimoco", route: "/quota/catnat" },
    { code: "multirischi_beni", name: "Tutela Multirischi Danni ai Beni", route: "/quota/multirischi" },
  ];

  const goToProduct = (route) => navigate(`${route}?anagrafica=${selected.id}`);

  const suggeritiCodes = new Set((suggeriti || []).map((s) => s.code));
  const altriProdotti = ALL_PRODUCTS.filter((p) => !suggeritiCodes.has(p.code));
  const productsToShow = suggeriti && suggeriti.length > 0 ? altriProdotti : ALL_PRODUCTS;

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-28 md:pb-16" data-testid="new-pratica-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Nuova pratica</h1>
        <p className="mt-1 text-muted-foreground">Parti dal cliente: cerca o crea l'anagrafica, poi individua i prodotti adeguati.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex flex-1 items-center gap-2">
            <div className={`flex h-9 items-center gap-2 rounded-full px-3 text-sm font-medium transition-colors
              ${step === s.n ? "bg-forest text-white" : step > s.n ? "bg-sage text-forest" : "bg-muted text-muted-foreground"}`}
              data-testid={`step-indicator-${s.n}`}>
              {step > s.n ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${step > s.n ? "bg-forest/40" : "bg-muted"}`} />}
          </div>
        ))}
      </div>

      {/* STEP 1 — CLIENTE */}
      {step === 1 && (
        <Card className="rounded-xl border-border/60 p-5">
          {!creating ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-forest/70">Cerca cliente</Label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="pratica-search-input" className="pl-9" autoFocus
                    placeholder="Codice Fiscale, Partita IVA o ragione sociale…"
                    value={query} onChange={(e) => setQuery(e.target.value)} />
                  {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground">Digita almeno 2 caratteri. La ricerca evita di creare doppioni dello stesso cliente.</p>
              </div>

              {results.length > 0 && (
                <div className="space-y-2" data-testid="pratica-search-results">
                  {results.map((a) => (
                    <button key={a.id} data-testid={`pratica-result-${a.id}`} onClick={() => pickCliente(a)}
                      className="flex w-full items-center gap-3 rounded-lg border border-border/60 p-3 text-left transition-colors hover:border-forest/40 hover:bg-sage/30">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sage text-forest">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-forest">{a.ragione_sociale}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[a.partita_iva && `P.IVA ${a.partita_iva}`, a.codice_fiscale && `CF ${a.codice_fiscale}`, a.comune].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-terracotta" />
                    </button>
                  ))}
                </div>
              )}

              {query.trim().length >= 2 && !searching && results.length === 0 && (
                <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground" data-testid="pratica-no-results">
                  Nessun cliente trovato per «{query}». Puoi crearne uno nuovo.
                </p>
              )}

              <Button variant="outline" data-testid="pratica-new-client-button"
                onClick={() => { setCreating(true); setForm((f) => ({ ...EMPTY, ragione_sociale: /^\d/.test(query) ? "" : query })); }}
                className="w-full gap-2 rounded-xl border-forest/30 text-forest hover:bg-sage/40">
                <UserPlus className="h-4 w-4" /> Nuovo cliente
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-forest/20 bg-sage/40 p-3">
                <Label className="text-xs font-semibold uppercase tracking-wide text-forest/70">Recupero automatico da Partita IVA</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input data-testid="pratica-vat-input" value={vatQuery} placeholder="Es. 02909120921"
                    onChange={(e) => setVatQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookupVat()} />
                  <Button type="button" data-testid="pratica-vat-button" onClick={lookupVat} disabled={looking}
                    className="shrink-0 gap-2 rounded-lg bg-forest hover:bg-forest-hover">
                    {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Recupera
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Ragione sociale *</Label>
                <Input data-testid="pratica-ragione-sociale" value={form.ragione_sociale} onChange={set("ragione_sociale")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipologia</Label>
                  <Select value={form.cluster} onValueChange={(v) => setForm({ ...form, cluster: v, tipo_ente: v })}>
                    <SelectTrigger data-testid="pratica-cluster"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{CLUSTERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Referente</Label><Input value={form.referente} onChange={set("referente")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input data-testid="pratica-cf" value={form.codice_fiscale} onChange={set("codice_fiscale")} /></div>
                <div className="space-y-1.5"><Label>Partita IVA</Label><Input data-testid="pratica-piva" value={form.partita_iva} onChange={set("partita_iva")} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>CAP</Label><Input value={form.cap} onChange={set("cap")} /></div>
                <div className="space-y-1.5"><Label>Comune</Label><Input value={form.comune} onChange={set("comune")} /></div>
                <div className="space-y-1.5"><Label>Prov.</Label><Input value={form.provincia} onChange={set("provincia")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
                <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} /></div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <Button variant="ghost" onClick={() => setCreating(false)} className="gap-2 text-muted-foreground">
                  <ArrowLeft className="h-4 w-4" /> Torna alla ricerca
                </Button>
                <Button onClick={saveCliente} disabled={saving || !form.ragione_sociale} data-testid="pratica-save-client"
                  className="gap-2 bg-terracotta hover:bg-terracotta-hover">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Salva e continua
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* STEP 2 — NEEDS ANALYSIS */}
      {step === 2 && selected && (
        <Card className="rounded-xl border-border/60 p-5">
          <div className="mb-4 flex items-center gap-3 rounded-lg bg-sage/40 p-3">
            <ShieldCheck className="h-5 w-5 text-forest" />
            <div className="text-sm">
              <span className="font-medium text-forest">{selected.ragione_sociale}</span>
              <span className="text-muted-foreground"> — analisi delle esigenze (facoltativa, salvata come promemoria consulenziale).</span>
            </div>
          </div>

          <div className="space-y-5">
            {questions.map((q) => (
              <div key={q.code} data-testid={`na-question-${q.code}`}>
                <Label className="font-medium text-forest">{q.label}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.options.map((o) => {
                    const active = q.type === "multi"
                      ? (answers[q.code] || []).includes(o.value)
                      : answers[q.code] === o.value;
                    return (
                      <button key={o.value} type="button"
                        data-testid={`na-${q.code}-${o.value}`}
                        onClick={() => q.type === "multi" ? toggleMulti(q.code, o.value) : setSingle(q.code, o.value)}
                        className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors
                          ${active ? "border-forest bg-forest text-white" : "border-border/70 bg-white text-forest hover:border-forest/40"}`}>
                        {active && <Check className="mr-1 inline h-3.5 w-3.5" />}{o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" data-testid="na-skip" onClick={skipNeeds} className="text-muted-foreground">
              Salta e vai ai prodotti
            </Button>
            <Button onClick={submitNeeds} disabled={savingNa || answeredCount === 0} data-testid="na-submit"
              className="gap-2 bg-terracotta hover:bg-terracotta-hover">
              {savingNa ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Suggerisci prodotti
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 3 — PRODOTTI */}
      {step === 3 && selected && (
        <div className="space-y-4">
          {suggeriti && suggeriti.length > 0 && (
            <div>
              <h2 className="mb-2 flex items-center gap-2 font-heading text-lg font-semibold text-forest">
                <Sparkles className="h-5 w-5 text-terracotta" /> Prodotti adeguati per {selected.ragione_sociale}
              </h2>
              <div className="grid gap-3">
                {suggeriti.map((s) => (
                  <button key={s.code} data-testid={`suggested-${s.code}`} onClick={() => goToProduct(s.route)}
                    className="group flex items-center gap-4 rounded-xl border-2 border-forest/30 bg-sage/20 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-forest/50 hover:shadow-md">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-semibold text-forest">{s.name}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium
                          ${s.level === "consigliato" ? "bg-forest text-white" : "bg-amber-100 text-amber-800"}`}>
                          {s.level === "consigliato" ? "Consigliato" : "Da valutare"}
                        </span>
                      </div>
                      <div className="mt-0.5 text-sm text-muted-foreground">{s.reason}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 shrink-0 text-terracotta transition-transform group-hover:translate-x-1" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {suggeriti && suggeriti.length === 0 && (
            <Card className="rounded-xl border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="no-suggestions">
              Nessun prodotto suggerito dalle risposte. Scegli tra tutti i prodotti disponibili.
            </Card>
          )}

          {productsToShow.length > 0 && (
            <div>
              <h2 className="mb-2 font-heading text-lg font-semibold text-forest">
                {suggeriti && suggeriti.length > 0 ? "Altri prodotti" : "Scegli il prodotto"}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {productsToShow.map((p) => (
                  <button key={p.code} data-testid={`product-${p.code}`} onClick={() => goToProduct(p.route)}
                    className="group flex items-center gap-3 rounded-xl border border-border/60 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-forest/40 hover:shadow-sm">
                    <span className="font-medium text-forest">{p.name}</span>
                    <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-terracotta transition-transform group-hover:translate-x-1" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <Button variant="ghost" onClick={() => setStep(2)} className="gap-2 text-muted-foreground">
            <ArrowLeft className="h-4 w-4" /> Torna all'analisi esigenze
          </Button>
        </div>
      )}
    </div>
  );
}
